import { fmtScale, SCALE_OPTIONS } from '@entities/geodesy';
import {
  ChoiceCard,
  ChoiceGrid,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Fieldset,
  Notice,
  NumInput,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useSettingsSaver, useStepDirty, useWizard } from './wizard';

const SCALE_NOTES: Readonly<Record<number, string>> = {
  20: 'одна конструкция, анкерные блоки',
  25: 'ростверк, фундамент марки',
  50: 'небольшие здания, узлы',
  100: 'здания, фундаменты',
  200: 'свайные поля, каркасы',
  250: 'крупные корпуса',
  500: 'площадки, планировка',
  1000: 'генплан, сети',
};

/** Шаг 3. Масштаб чертежа — не мельче масштаба рабочих чертежей (ГОСТ Р 51872 п.5.8). */
export function StepScale() {
  const { scheme, readOnly } = useWizard();
  const scale = useDraft(scheme.settings.scale);
  useStepDirty('scale', scale.dirty && !readOnly);
  const saver = useSettingsSaver();

  const d = scale.draft;
  const hint = scheme.sheet?.scale_hint ?? null;
  const rd = d.design_denominator ?? hint;
  const tooSmall = d.denominator != null && rd != null && d.denominator > rd;
  const validRd = d.design_denominator == null || d.design_denominator > 0;

  return (
    <StepShell
      n={3}
      lead="Масштаб указывается после наименования изображения (п.5.9). «Авто» подбирает масштаб под размер области чертежа."
      save={{
        dirty: scale.dirty && !readOnly,
        busy: saver.busy,
        error: saver.error,
        blockedReason: !validRd ? 'Масштаб РД должен быть положительным числом.' : null,
        onSave: async () => {
          const ok = await saver.save((cur) => ({
            ...cur,
            scale: {
              denominator: d.denominator,
              design_denominator:
                d.design_denominator == null ? null : Math.round(d.design_denominator),
            },
          }));
          if (ok) scale.reset();
          return ok;
        },
        onReset: scale.reset,
      }}
    >
      <Fieldset disabled={readOnly}>
        <Stack $gap={4}>
          <Panel>
            <PanelHead>
              <PanelTitle>Масштаб исполнительной схемы</PanelTitle>
            </PanelHead>
            <ChoiceGrid $min={132} role="radiogroup" aria-label="Масштаб схемы">
              {SCALE_OPTIONS.map((n) => (
                <ChoiceCard
                  key={n}
                  name="geo-scale"
                  checked={d.denominator === n}
                  onSelect={() => scale.set({ ...d, denominator: n })}
                  title={fmtScale(n)}
                >
                  {SCALE_NOTES[n]}
                </ChoiceCard>
              ))}
              <ChoiceCard
                name="geo-scale"
                checked={d.denominator == null}
                onSelect={() => scale.set({ ...d, denominator: null })}
                title="Авто"
              >
                по области чертежа
              </ChoiceCard>
            </ChoiceGrid>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Масштаб рабочих чертежей</PanelTitle>
            </PanelHead>
            <FieldGrid $min={240}>
              <FieldLabel>
                Знаменатель масштаба РД, 1:N
                <NumInput
                  value={d.design_denominator}
                  onValue={(v) => scale.set({ ...d, design_denominator: v })}
                  placeholder={hint ? String(hint) : 'например, 200'}
                />
                <FieldNote>
                  {hint
                    ? `С листа распознан ${fmtScale(hint)}. Пусто — берём распознанный.`
                    : 'С листа масштаб не распознан — укажите по штампу РД.'}
                </FieldNote>
              </FieldLabel>
            </FieldGrid>
          </Panel>

          {tooSmall && rd != null && d.denominator != null && (
            <Notice $tone="danger" role="alert">
              <strong>
                {fmtScale(d.denominator)} мельче масштаба РД {fmtScale(rd)}.
              </strong>
              ГОСТ Р 51872 п.5.8: масштаб исполнительной схемы не мельче масштаба рабочих чертежей.
              Контроль не пропустит такую схему в ПТО.
            </Notice>
          )}
          {d.denominator == null && rd != null && (
            <Notice $tone="neutral" role="status">
              При «Авто» следите, чтобы масштаб не вышел мельче РД ({fmtScale(rd)}) — это покажет
              контроль на шаге 8. Если чертёж не помещается, увеличьте область чертежа на шаге 5 или
              формат листа на шаге 4.
            </Notice>
          )}
        </Stack>
      </Fieldset>
    </StepShell>
  );
}
